import i18n from '@/shared/i18n/i18n';

import { formatDateTime, formatPrice } from './format';

// ── Types ────────────────────────────────────────────────────────────────────

export type ReadinessItemStatus = 'ok' | 'warn' | 'blocked' | 'neutral';

export interface ReadinessItem {
  /** Short label for the readiness dimension (e.g. "Product card"). */
  label: string;
  /** Human-readable explanation of the current value/gate state. */
  detail: string;
  status: ReadinessItemStatus;
}

export type OverallReadiness = 'ready' | 'partial' | 'blocked';

export interface ReadinessResult {
  items: ReadinessItem[];
  overall: OverallReadiness;
  /** The first failing item that sets the overall verdict (null when ready). */
  primaryBlocker: ReadinessItem | null;
}

/**
 * Generic input shape — intentionally string-typed so both `ProductSummary`
 * and live form state can feed it without casting.
 */
export interface ReadinessInput {
  /**
   * `cardStatus` value. Defaults to 'ACTIVE' when absent (backend default).
   * Expected values: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
   */
  cardStatus?: string | null;
  /**
   * `commercialAvailability` value. Defaults to treating as AVAILABLE when absent.
   * Expected values: 'AVAILABLE' | 'PREORDER' | 'CHECK_AVAILABILITY' | 'COMING_SOON'
   *                  | 'OUT_OF_STOCK' | 'TEMPORARILY_UNAVAILABLE' | 'ARCHIVED'
   */
  commercialAvailability?: string | null;
  /** Whether the product is published to at least one sales channel. */
  published: boolean;
  /** Optional list of published channel codes for the detail label. */
  publishedChannels?: string[];
  /** Numeric price. `0` / `null` / `undefined` → price gate fails. */
  price?: number | null;
  /**
   * Card only (design spec §8.8) — when there is no governing base price, the base slot's newest
   * non-ACTIVE submission, told apart by state. `REJECTED` collapses into `'BLOCKED'`: both read
   * as "the last submitted price can't be sold" from the readiness row's point of view — the
   * distinction stays on the card's own badge/drawer, which name the two separately. Ignored once
   * `price` resolves (a governing price always wins — §8.8 row 1).
   */
  pricePending?: 'REQUIRES_APPROVAL' | 'PENDING' | 'BLOCKED';
  /**
   * Card only (design spec §8.8) — the base slot's scheduled (future-dated ACTIVE) line, shown only
   * while there is no governing price AND no pending/negative submission (`pricePending` takes
   * precedence — an open governance question is more actionable than a future date).
   */
  priceScheduled?: { at: string; amount: number };
  /**
   * Card only (design spec §4/§8.8) — true when the viewer lacks `catalog.pricing.view`. The price
   * row must not claim "price missing" when it never had the right to look: this short-circuits
   * `price`/`pricePending`/`priceScheduled` entirely and renders as `'neutral'`, not `'warn'`, so it
   * never taints the overall verdict (the same treatment the publication gate already gives an
   * archived product's "not published" row, for the same reason — it isn't a genuine failure).
   */
  priceAccessDenied?: boolean;
}

// ── Core computation ─────────────────────────────────────────────────────────

/**
 * Computes a four-gate readiness checklist for publication-and-sale readiness.
 *
 * The four gates mirror the backend's product-visibility chain:
 *  1. `cardStatus` — authoring gate (storefront eligibility)
 *  2. `commercialAvailability` — buyer-intent gate (primary CustomerState signal;
 *     when set it overrides stockStatus in ProductDocumentMapper)
 *  3. `published` — channel publication gate
 *  4. `price` — price governance gate (proxy: price > 0)
 *
 * Both `ProductDetailDrawer` and the hero section of `ProductEditPage` use this.
 * For the edit page, feed `draft.commercialAvailability` and the current base price loaded from
 * price-engine so the panel responds to the source of truth for prices.
 *
 * Labels/details are resolved via the i18next singleton (not the `useTranslation` hook,
 * since this is a plain function, not a component) — they follow the active language at
 * call time. Callers are components that already use `useTranslation('catalog')` for their
 * own text, so a language switch re-renders them and this recomputes with the new language.
 */
export function computeReadiness(input: ReadinessInput): ReadinessResult {
  const t = i18n.t.bind(i18n);
  const card = input.cardStatus ?? 'ACTIVE'; // backend default
  const comm = input.commercialAvailability;
  const { published, price } = input;

  const items: ReadinessItem[] = [];

  // ── 1. Authoring gate — cardStatus ────────────────────────────────────────
  const cardLabel = t('catalog:readiness.card.label');
  if (card === 'ACTIVE') {
    items.push({ label: cardLabel, detail: t('catalog:readiness.card.active'), status: 'ok' });
  } else if (card === 'DRAFT') {
    items.push({ label: cardLabel, detail: t('catalog:readiness.card.draft'), status: 'warn' });
  } else {
    // ARCHIVED
    items.push({ label: cardLabel, detail: t('catalog:readiness.card.archived'), status: 'blocked' });
  }

  // ── 2. Buyer-intent gate — commercialAvailability (PRIMARY buyer signal) ──
  const availabilityLabel = t('catalog:readiness.availability.label');
  if (!comm || comm === 'AVAILABLE') {
    items.push({ label: availabilityLabel, detail: t('catalog:readiness.availability.available'), status: 'ok' });
  } else if (comm === 'PREORDER') {
    items.push({ label: availabilityLabel, detail: t('catalog:readiness.availability.preorder'), status: 'ok' });
  } else if (comm === 'CHECK_AVAILABILITY') {
    items.push({
      label: availabilityLabel,
      detail: t('catalog:readiness.availability.checkAvailability'),
      status: 'warn',
    });
  } else if (comm === 'COMING_SOON') {
    items.push({
      label: availabilityLabel,
      detail: t('catalog:readiness.availability.comingSoon'),
      status: 'warn',
    });
  } else if (comm === 'OUT_OF_STOCK') {
    items.push({
      label: availabilityLabel,
      detail: t('catalog:readiness.availability.outOfStock'),
      status: 'warn',
    });
  } else if (comm === 'TEMPORARILY_UNAVAILABLE') {
    items.push({
      label: availabilityLabel,
      detail: t('catalog:readiness.availability.temporarilyUnavailable'),
      status: 'warn',
    });
  } else {
    // ARCHIVED
    items.push({
      label: availabilityLabel,
      detail: t('catalog:readiness.availability.archived'),
      status: 'blocked',
    });
  }

  // ── 3. Channel gate — published ───────────────────────────────────────────
  const publicationLabel = t('catalog:readiness.publication.label');
  const isHardBlocked = card === 'ARCHIVED' || comm === 'ARCHIVED';
  if (published) {
    const n = input.publishedChannels?.length ?? 0;
    items.push({
      label: publicationLabel,
      detail:
        n > 0
          ? t('catalog:readiness.publication.publishedWithChannels', { count: n })
          : t('catalog:readiness.publication.published'),
      status: 'ok',
    });
  } else if (isHardBlocked) {
    // Don't penalise the score twice when archiving already sets the verdict.
    items.push({
      label: publicationLabel,
      detail: t('catalog:readiness.publication.notPublishedArchived'),
      status: 'neutral',
    });
  } else {
    items.push({
      label: publicationLabel,
      detail: t('catalog:readiness.publication.notPublished'),
      status: 'warn',
    });
  }

  // ── 4. Price gate — proxy: price > 0 ─────────────────────────────────────
  const priceLabel = t('catalog:readiness.price.label');
  if (input.priceAccessDenied) {
    // Neutral, not warn (design spec §4/§8.8): the viewer simply isn't allowed to look — showing
    // "missing" would misstate a fact nobody checked. Excluded from the overall verdict below,
    // exactly like the publication gate's own "not published (archived)" neutral row.
    items.push({ label: priceLabel, detail: t('catalog:readiness.price.noAccess'), status: 'neutral' });
  } else if (price != null && price > 0) {
    items.push({ label: priceLabel, detail: formatPrice(price), status: 'ok' });
  } else if (input.pricePending === 'REQUIRES_APPROVAL') {
    items.push({ label: priceLabel, detail: t('catalog:readiness.price.pendingApproval'), status: 'warn' });
  } else if (input.pricePending === 'PENDING') {
    items.push({ label: priceLabel, detail: t('catalog:readiness.price.pendingCheck'), status: 'warn' });
  } else if (input.pricePending === 'BLOCKED') {
    items.push({ label: priceLabel, detail: t('catalog:readiness.price.blocked'), status: 'warn' });
  } else if (input.priceScheduled) {
    items.push({
      label: priceLabel,
      detail: t('catalog:readiness.price.scheduledOnly', {
        date: formatDateTime(input.priceScheduled.at),
        price: formatPrice(input.priceScheduled.amount),
      }),
      status: 'warn',
    });
  } else {
    items.push({ label: priceLabel, detail: t('catalog:readiness.price.missing'), status: 'warn' });
  }

  // ── Overall verdict ───────────────────────────────────────────────────────
  const hasBlocked = items.some((i) => i.status === 'blocked');
  const hasWarn = items.some((i) => i.status === 'warn');
  const overall: OverallReadiness = hasBlocked ? 'blocked' : hasWarn ? 'partial' : 'ready';
  const primaryBlocker = items.find((i) => i.status === 'blocked' || i.status === 'warn') ?? null;

  return { items, overall, primaryBlocker };
}

// ── Verdict helpers (used by both Drawer and EditPage hero) ──────────────────

export function overallSeverity(overall: OverallReadiness): 'success' | 'warning' | 'error' {
  if (overall === 'ready') return 'success';
  if (overall === 'partial') return 'warning';
  return 'error';
}

export function overallMessage(overall: OverallReadiness): string {
  const t = i18n.t.bind(i18n);
  if (overall === 'ready') return t('catalog:readiness.overall.ready');
  if (overall === 'partial') return t('catalog:readiness.overall.partial');
  return t('catalog:readiness.overall.blocked');
}
